// src/api/billing.ts
import { api } from "../api";

// ---------- Types ----------

export type PlanTier = "free" | "pro" | "business" | "enterprise";
export type BillingFrequency = "monthly" | "annual";

export type TrialStatus = {
  plan: PlanTier;
  isTrialing: boolean;
  trialExpired: boolean;
  trialEndsAt: string | null;
  daysRemaining: number;
  hasSubscription: boolean;
};

export type TierLimits = {
  signaturesPerMonth: number;
  maxTemplates: number;
  maxUsers: number;
  aiFeatures: boolean;
  quickSend: boolean;
  unlimitedQuickSendTemplates: boolean;
  fullAuditTrail: boolean;
  prioritySupport: boolean;
};

export type BillingStatus = {
  success: boolean;
  plan: PlanTier;
  planFrequency: BillingFrequency | null;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  planExpiresAt: string | null;
  stripeCustomerId: string | null;
  isTrialing: boolean;
  trialExpired: boolean;
  trialEndsAt: string | null;
  daysRemaining: number;
  limits: TierLimits;
  usage: { signaturesThisMonth: number; templateCount: number };
};

export type ProrationEstimate = {
  success: boolean;
  amountDue: number;
  credit: number;
  newCharge: number;
  currency: string;
  nextBillingDate: string | null;
};

// ---------- API calls ----------

export const fetchTrialStatus = async (): Promise<TrialStatus> => {
  const res = await api.get("/api/billing/trial-status");
  return res.data;
};

export const fetchBillingStatus = async (): Promise<BillingStatus> => {
  const res = await api.get("/api/billing/status");
  return res.data;
};

export const createSubscription = async (
  tier: PlanTier,
  frequency: BillingFrequency,
  paymentMethodId: string
): Promise<any> => {
  const res = await api.post("/api/billing/create-subscription", {
    tier,
    frequency,
    paymentMethodId,
  });
  return res.data;
};

export const changePlan = async (
  tier: PlanTier,
  frequency: BillingFrequency
): Promise<any> => {
  const res = await api.post("/api/billing/change-plan", { tier, frequency });
  return res.data;
};

export const estimateProration = async (
  tier: PlanTier,
  frequency: BillingFrequency
): Promise<ProrationEstimate> => {
  const res = await api.get("/api/billing/estimate-proration", {
    params: { tier, frequency },
  });
  return res.data;
};

export const cancelSubscription = async (): Promise<any> => {
  const res = await api.post("/api/billing/cancel");
  return res.data;
};

export const contactEnterprise = async (data: {
  companyName: string;
  teamSize: string;
  message: string;
}): Promise<any> => {
  const res = await api.post("/api/billing/contact-enterprise", data);
  return res.data;
};
